<script>
import * as m from '@app/paraglide/messages.js';
import { auth } from "@app/configs/firebase.js";
import { signOut } from "firebase/auth";
import { environment } from "@app/store/envStore.svelte.js";
import { toast } from "@zerodevx/svelte-toast";
import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";

const ipcRenderer = window.ipcRenderer;

const INCLUDED = "included";
const NOT_INCLUDED = "not";
const ADDON = "addon";

// Stripe Payment Link URLs
const paymentLinks = {
    starter: {
        monthly: "https://buy.stripe.com/00waEXePodDm4dA8y8fQI03",
        yearly: "https://buy.stripe.com/aFa14n8r0czicK629KfQI04"
    },
    pro: {
        monthly: "https://buy.stripe.com/9B6aEX0YygPybG201CfQI05",
        yearly: "https://buy.stripe.com/14AfZh22Cbve8tQ4hSfQI06"
    }
};

// Pricing
const pricing = {
    starter: {
        monthly: { amount: 30 },
        yearly: { amount: 300 }
    },
    pro: {
        monthly: { amount: 60 },
        yearly: { amount: 600 }
    }
};

// State
let billingPeriod = $state("monthly"); // "monthly" or "yearly"
let loading = $state(false);

const periodLabel = $derived(
    billingPeriod === "yearly" ? m.signup_period_year() : m.signup_period_month()
);

const starterFeatures = $derived.by(() => {
    const minutes = billingPeriod === "yearly" ? "3600" : "300";
    return [
        m.signup_feature_starter_1(),
        m.signup_feature_starter_2({ minutes, period: periodLabel }),
        m.signup_feature_starter_3(),
        m.signup_feature_starter_4(),
        m.signup_feature_starter_5(),
        m.signup_feature_starter_6(),
        m.signup_feature_starter_7(),
        m.signup_feature_starter_8(),
        m.signup_feature_starter_9()
    ];
});

const proFeatures = $derived.by(() => {
    const minutes = billingPeriod === "yearly" ? "7200" : "600";
    const characters = billingPeriod === "yearly" ? "6,000,000" : "500,000";
    return [
        m.signup_feature_pro_1(),
        m.signup_feature_pro_2({ minutes, period: periodLabel }),
        m.signup_feature_pro_3(),
        m.signup_feature_pro_4({ characters, period: periodLabel }),
        m.signup_feature_pro_5(),
        m.signup_feature_pro_6(),
        m.signup_feature_pro_7(),
        m.signup_feature_pro_8(),
        m.signup_feature_pro_9()
    ];
});

const compareSections = $derived.by(() => {
    const minUnit = m.signup_compare_unit_min();
    const charUnit = m.signup_compare_unit_chars();
    const starterCaptionMins = billingPeriod === "yearly" ? "3600" : "300";
    const proCaptionMins = billingPeriod === "yearly" ? "7200" : "600";
    const proTranslationChars = billingPeriod === "yearly" ? "6,000,000" : "500,000";
    const speechChars = billingPeriod === "yearly" ? "9,000,000" : "750,000";
    const perMinute = m.signup_compare_per_minute();

    return [
        {
            title: m.signup_compare_section_who(),
            showHeader: false,
            rows: [
                {
                    label: m.signup_compare_section_who(),
                    starter: m.signup_compare_who_starter(),
                    pro: m.signup_compare_who_pro()
                }
            ]
        },
        {
            title: m.signup_compare_section_ai(),
            rows: [
                {
                    label: m.signup_compare_captioning(),
                    starter: m.signup_compare_allowance({
                        amount: starterCaptionMins,
                        unit: minUnit,
                        period: periodLabel
                    }),
                    pro: m.signup_compare_allowance({
                        amount: proCaptionMins,
                        unit: minUnit,
                        period: periodLabel
                    })
                },
                {
                    label: m.signup_compare_translation(),
                    starter: NOT_INCLUDED,
                    pro: m.signup_compare_allowance({
                        amount: proTranslationChars,
                        unit: charUnit,
                        period: periodLabel
                    })
                },
                {
                    label: m.signup_compare_shot_descriptions(),
                    starter: NOT_INCLUDED,
                    pro: perMinute
                },
                {
                    label: m.signup_compare_ad_dv(),
                    starter: NOT_INCLUDED,
                    pro: perMinute
                },
                {
                    label: m.signup_compare_speech(),
                    starter: m.signup_compare_allowance({
                        amount: speechChars,
                        unit: charUnit,
                        period: periodLabel
                    }),
                    pro: m.signup_compare_allowance({
                        amount: speechChars,
                        unit: charUnit,
                        period: periodLabel
                    })
                },
                {
                    label: m.signup_compare_shot_change(),
                    starter: NOT_INCLUDED,
                    pro: INCLUDED
                },
                {
                    label: m.signup_compare_auto_sync(),
                    starter: NOT_INCLUDED,
                    pro: INCLUDED
                }
            ]
        },
        {
            title: m.signup_compare_section_import(),
            rows: [
                { label: m.signup_compare_transcript_import(), starter: INCLUDED, pro: INCLUDED },
                { label: m.signup_compare_subtitle_import(), starter: INCLUDED, pro: INCLUDED },
                { label: m.signup_compare_playback(), starter: INCLUDED, pro: INCLUDED },
                { label: m.signup_compare_players(), starter: INCLUDED, pro: INCLUDED },
                { label: m.signup_compare_custom_import(), starter: INCLUDED, pro: INCLUDED }
            ]
        },
        {
            title: m.signup_compare_section_export(),
            rows: [
                { label: m.signup_compare_transcript_export(), starter: INCLUDED, pro: INCLUDED },
                { label: m.signup_compare_dialogue_export(), starter: INCLUDED, pro: INCLUDED },
                { label: m.signup_compare_embed(), starter: NOT_INCLUDED, pro: INCLUDED },
                { label: m.signup_compare_basic_export(), starter: INCLUDED, pro: INCLUDED },
                { label: m.signup_compare_advanced_export(), starter: NOT_INCLUDED, pro: INCLUDED },
                { label: m.signup_compare_ad_export(), starter: ADDON, pro: ADDON },
                { label: m.signup_compare_ead_export(), starter: ADDON, pro: ADDON }
            ]
        },
        {
            title: m.signup_compare_section_additional(),
            rows: [
                { label: m.signup_compare_timeline(), starter: INCLUDED, pro: INCLUDED },
                { label: m.signup_compare_foot_pedal(), starter: INCLUDED, pro: INCLUDED },
                { label: m.signup_compare_desktop(), starter: INCLUDED, pro: INCLUDED },
                { label: m.signup_compare_web(), starter: INCLUDED, pro: INCLUDED },
                { label: m.signup_compare_multichannel(), starter: INCLUDED, pro: INCLUDED },
                { label: m.signup_compare_qc(), starter: INCLUDED, pro: INCLUDED },
                { label: m.signup_compare_knp(), starter: NOT_INCLUDED, pro: INCLUDED },
                { label: m.signup_compare_team(), starter: NOT_INCLUDED, pro: INCLUDED },
                { label: m.signup_compare_replacement(), starter: INCLUDED, pro: INCLUDED },
                {
                    label: m.signup_compare_users(),
                    starter: m.signup_compare_users_starter(),
                    pro: m.signup_compare_users_pro()
                }
            ]
        },
        {
            title: m.signup_compare_section_support(),
            rows: [
                { label: m.signup_compare_chat(), starter: INCLUDED, pro: INCLUDED },
                { label: m.signup_compare_updates(), starter: INCLUDED, pro: INCLUDED },
                { label: m.signup_compare_training(), starter: NOT_INCLUDED, pro: INCLUDED },
                { label: m.signup_compare_priority(), starter: NOT_INCLUDED, pro: NOT_INCLUDED },
                { label: m.signup_compare_account_manager(), starter: NOT_INCLUDED, pro: NOT_INCLUDED }
            ]
        }
    ];
});

// Handle plan selection
async function selectPlan(plan) {
    try {
        loading = true;

        // Get user email from Firebase auth
        const user = auth.currentUser;
        if (!user) {
            toast.push(m.signup_login_required_toast(), {
                classes: ["toast-danger"]
            });
            loading = false;
            return;
        }

        const email = user.email;
        
        // Get the payment link URL
        const paymentLinkUrl = paymentLinks[plan][billingPeriod];
        
        // Build URL with prefilled email
        const checkoutUrl = `${paymentLinkUrl}?prefilled_email=${encodeURIComponent(email)}`;

        // Handle Electron vs Web
        if (environment.electron) {
            // Show alert for Electron users
            const result = await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.ok,
                titleText: m.signup_swal_title(),
                html: m.signup_swal_message(),
                icon: "info",
                confirmButtonText: m.signup_swal_confirm_button(),
                allowOutsideClick: false,
                allowEscapeKey: false,
            });

            if (result.isConfirmed) {
                // Open URL in external browser
                if (ipcRenderer) {
                    await ipcRenderer.invoke("openExternal", checkoutUrl);
                }
                
                // Quit the application
                if (ipcRenderer) {
                    await ipcRenderer.invoke("quit");
                }
            }
        } else {
            // For web, redirect to Stripe checkout
            window.location.href = checkoutUrl;
        }
    } catch (error) {
        console.error("Error starting checkout:", error);
        toast.push(m.signup_checkout_failed_toast({ message: error.message }), {
            classes: ["toast-danger"]
        });
        loading = false;
    }
}

// Handle checkout cancel from query params
function handleCheckoutCancel() {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("checkout") === "cancel") {
        toast.push(m.signup_checkout_cancelled_toast(), {
            classes: ["toast-info"]
        });
        // Clear the query param
        window.history.replaceState({}, "", window.location.pathname);
    }
}

function getPriceDisplay(plan, period) {
    const amount = pricing[plan][period].amount;
    if (period === "monthly") {
        return m.signup_price_monthly_display({ amount });
    }

    const monthlyEquivalent = Math.round(amount / 12);
    return m.signup_price_yearly_display({ amount, monthlyEquivalent });
}

function compareCellLabel(value) {
    if (value === INCLUDED) {
        return m.signup_compare_included();
    }
    if (value === NOT_INCLUDED) {
        return m.signup_compare_not_included();
    }
    if (value === ADDON) {
        return m.signup_compare_optional_addon();
    }
    return value;
}

// Switch accounts - log out and return to login page
async function switchAccounts() {
    try {
        await signOut(auth);
    } catch (error) {
        console.error("Error signing out:", error);
    } finally {
        window.location.reload();
    }
}

// Run on mount
handleCheckoutCancel();
</script>

{#snippet compareValue(value)}
    {#if value === INCLUDED}
        <i class="bi bi-check-circle-fill check-icon me-2"></i>
    {:else if value === NOT_INCLUDED}
        <i class="bi bi-x-circle not-included-icon me-2"></i>
    {:else if value === ADDON}
        <i class="bi bi-plus-circle addon-icon me-2"></i>
    {/if}
    {compareCellLabel(value)}
{/snippet}

<!-- HTML TEMPLATE -->
<div id="SignupScreen">
    <div class="container SignupPageWrapper">
        <div class="row justify-content-center">
            <div class="col-10 SignupFormWrapper">
                <div class="text-center mb-5">
                    <img src="./cc-logo.png" alt={m.app_logo_alt()} class="logo mb-4" />
                    <h1 class="display-5 fw-bold mb-3">{m.signup_title()}</h1>
                    <h2 class="subtitle">{m.signup_subtitle()}</h2>
                    <p class="description">{m.signup_description()}</p>
                </div>

                <!-- Billing Period Toggle -->
                <div class="d-flex justify-content-center mb-5">
                    <div class="btn-group billing-toggle" role="group">
                        <button 
                            type="button"
                            class="btn {billingPeriod === 'monthly' ? 'btn-primary' : 'btn-outline-primary'}"
                            onclick={() => billingPeriod = 'monthly'}
                            disabled={loading}>
                            {m.signup_billing_monthly()}
                        </button>
                        <button 
                            type="button"
                            class="btn {billingPeriod === 'yearly' ? 'btn-primary' : 'btn-outline-primary'}"
                            onclick={() => billingPeriod = 'yearly'}
                            disabled={loading}>
                            {m.signup_billing_yearly()}
                        </button>
                    </div>
                </div>

                <!-- Plan Cards -->
                <div class="row g-4 mb-4">
                    <!-- Starter Plan -->
                    <div class="col-md-6">
                        <div class="card h-100">
                            <div class="card-body d-flex flex-column">
                                <h3 class="card-title h4 mb-1">{m.signup_plan_starter()}</h3>
                                <p class="plan-audience mb-3">{m.signup_plan_starter_audience()}</p>
                                <div class="mb-4">
                                    <div class="price-display">
                                        {getPriceDisplay("starter", billingPeriod)}
                                    </div>
                                </div>
                                <ul class="list-unstyled mb-4 flex-grow-1 feature-list">
                                    {#each starterFeatures as feature (feature)}
                                        <li class="mb-2">
                                            <i class="bi bi-check-circle-fill check-icon me-2"></i>
                                            {feature}
                                        </li>
                                    {/each}
                                </ul>
                                <button 
                                    class="btn btn-primary btn-lg w-100"
                                    onclick={() => selectPlan('starter')}
                                    disabled={loading}>
                                    {#if loading}
                                        <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    {/if}
                                    {m.signup_start_trial_button()}
                                </button>
                                <p class="text-center trial-note mt-3 mb-0">{m.signup_trial_note()}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Pro Plan -->
                    <div class="col-md-6">
                        <div class="card h-100 card-featured position-relative">
                            <div class="position-absolute top-0 start-50 translate-middle">
                                <span class="badge-popular">{m.signup_most_popular_badge()}</span>
                            </div>
                            <div class="card-body d-flex flex-column pt-5">
                                <h3 class="card-title h4 mb-1">{m.signup_plan_pro()}</h3>
                                <p class="plan-audience mb-3">{m.signup_plan_pro_audience()}</p>
                                <div class="mb-4">
                                    <div class="price-display">
                                        {getPriceDisplay("pro", billingPeriod)}
                                    </div>
                                </div>
                                <p class="feature-includes mb-2">{m.signup_plan_pro_includes()}</p>
                                <ul class="list-unstyled mb-4 flex-grow-1 feature-list">
                                    {#each proFeatures as feature (feature)}
                                        <li class="mb-2">
                                            <i class="bi bi-check-circle-fill check-icon me-2"></i>
                                            {feature}
                                        </li>
                                    {/each}
                                </ul>
                                <button 
                                    class="btn btn-primary btn-lg w-100"
                                    onclick={() => selectPlan('pro')}
                                    disabled={loading}>
                                    {#if loading}
                                        <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    {/if}
                                    {m.signup_start_trial_button()}
                                </button>
                                <p class="text-center trial-note mt-3 mb-0">{m.signup_trial_note()}</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div>
                    <p class="footnotes">
                        {@html m.signup_footnote_transcription()}<br>
                        {@html m.signup_footnote_translation()}<br>
                        {@html m.signup_footnote_users()}<br>
                        {@html m.signup_footnote_embedding()}<br>
                        {@html m.signup_footnote_ad()}
                    </p>
                </div>

                <div id="compare-plans" class="compare-plans">
                    <h2 class="compare-title h4 mb-3">{m.signup_compare_title()}</h2>
                    <div class="table-responsive">
                        <table class="table compare-table mb-0">
                            <thead>
                                <tr>
                                    <th scope="col"></th>
                                    <th scope="col">{m.signup_plan_starter()}</th>
                                    <th scope="col">{m.signup_plan_pro()}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {#each compareSections as section (section.title)}
                                    {#if section.showHeader !== false}
                                        <tr class="compare-section-row">
                                            <th colspan="3">{section.title}</th>
                                        </tr>
                                    {/if}
                                    {#each section.rows as row (row.label)}
                                        <tr>
                                            <th scope="row">{row.label}</th>
                                            <td class:status-included={row.starter === INCLUDED} class:status-not={row.starter === NOT_INCLUDED}>
                                                {@render compareValue(row.starter)}
                                            </td>
                                            <td class:status-included={row.pro === INCLUDED} class:status-not={row.pro === NOT_INCLUDED}>
                                                {@render compareValue(row.pro)}
                                            </td>
                                        </tr>
                                    {/each}
                                {/each}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div class="text-center help-text">
                    {@html m.signup_help_choosing()}
                </div>

                <div class="text-center help-text fw-bold">
                    <div class="text-center help-text fw-bold s-pQUFMqLZdsz0">Wrong account? <a href="#!/" class="link-accent" onclick="{() => switchAccounts()}">Switch Accounts</a></div>
                </div>
            </div>
        </div>
    </div>
</div>

<style>
    #SignupScreen {
        position: relative;
        min-height: 100vh;
        width: 100vw;
        overflow-x: hidden;
        overflow-y: auto;
        color: #f0f0f0;
        font-family: 'Roboto', sans-serif;
        background: #000000;
        background: linear-gradient(154deg, rgba(0, 0, 0, 1) 0%, rgba(6, 71, 57, 1) 100%);
    }

    .SignupPageWrapper {
        min-height: 100vh;
        padding: 2rem 0;
        display: flex;
        flex-direction: column;
        justify-content: center;
    }

    .SignupFormWrapper {
        background: rgba(0, 0, 0, 0.45);
        backdrop-filter: blur(10px);
        border-radius: 15px;
        padding: 40px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
        border: 1px solid rgba(255, 255, 255, 0.1);
    }

    .logo {
        max-width: 200px;
        height: auto;
    }

    h1 {
        color: #f0f0f0;
    }

    .subtitle {
        color: rgba(240, 240, 240, 0.8);
        font-size: 2rem;
        font-weight: 400;
    }

    .description {
        color: rgba(240, 240, 240, 0.7);
    }

    /* Billing Toggle */
    .billing-toggle {
        background: rgba(255, 255, 255, 0.1);
        border-radius: 8px;
        padding: 4px;
    }

    .billing-toggle .btn {
        min-width: 120px;
        border-radius: 6px;
        font-weight: 500;
        transition: all 0.3s ease;
    }

    .billing-toggle .btn-primary {
        background: linear-gradient(135deg, #009978, #00853c);
        border-color: transparent;
    }

    .billing-toggle .btn-outline-primary {
        background: transparent;
        border-color: transparent;
        color: rgba(255, 255, 255, 0.7);
    }

    .billing-toggle .btn-outline-primary:hover {
        background: rgba(255, 255, 255, 0.1);
        color: #f0f0f0;
    }

    /* Cards */
    .card {
        background: rgba(0, 0, 0, 0.35);
        backdrop-filter: blur(10px);
        border-radius: 15px;
        border: 1px solid rgba(255, 255, 255, 0.1);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
        transition: transform 0.2s ease, box-shadow 0.2s ease;
    }

    .card-featured {
        border: 2px solid rgba(0, 153, 120, 0.6);
        background: rgba(0, 0, 0, 0.45);
    }

    .card-body {
        padding: 2rem;
    }

    .card-title {
        color: #f0f0f0;
        font-weight: 600;
    }

    .plan-audience {
        color: rgba(240, 240, 240, 0.65);
        font-size: 0.95rem;
    }

    .feature-includes {
        color: rgba(240, 240, 240, 0.9);
        font-weight: 600;
        font-size: 0.95rem;
    }

    /* Price Display */
    .price-display {
        font-size: 1.75rem;
        font-weight: 700;
        color: #0dfdb5;
    }

    /* Feature List */
    .feature-list {
        color: rgba(240, 240, 240, 0.85);
        font-size: 0.95rem;
    }

    .feature-list li {
        display: flex;
        align-items: flex-start;
    }

    .check-icon {
        color: #0dfdb5;
        flex-shrink: 0;
        margin-top: 2px;
    }

    .not-included-icon {
        color: rgba(255, 255, 255, 0.35);
        flex-shrink: 0;
        margin-top: 2px;
    }

    .addon-icon {
        color: rgba(13, 253, 181, 0.7);
        flex-shrink: 0;
        margin-top: 2px;
    }

    /* Badge */
    .badge-popular {
        background: linear-gradient(135deg, #009978, #00853c);
        color: #fff;
        font-weight: 600;
        font-size: 0.875rem;
        border-radius: 1rem;
        padding: 0.5rem 1rem;
        box-shadow: 0 4px 12px rgba(0, 153, 120, 0.4);
    }

    /* Buttons */
    .btn-primary {
        background: linear-gradient(135deg, #009978, #00853c);
        border: none;
        font-weight: 500;
        border-radius: 8px;
        padding: 12px 20px;
        transition: all 0.3s ease;
    }

    .btn-primary:hover:not(:disabled) {
        background: linear-gradient(135deg, #00b38a, #009948);
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(13, 253, 181, 0.3);
    }

    .btn-primary:disabled {
        background: rgba(0, 153, 120, 0.5);
        transform: none;
        box-shadow: none;
    }

    /* Trial Note */
    .trial-note {
        color: rgba(255, 255, 255, 0.6);
        font-size: 0.875rem;
    }

    /* Footnotes */
    .footnotes {
        color: rgba(255, 255, 255, 0.5);
        font-size: 0.8rem;
        line-height: 1.6;
    }

    /* Compare table */
    .compare-plans {
        margin-top: 2.5rem;
    }

    .compare-title {
        color: #f0f0f0;
        font-weight: 600;
        text-align: center;
    }

    .compare-table {
        --bs-table-bg: transparent;
        --bs-table-color: rgba(240, 240, 240, 0.85);
        --bs-table-border-color: rgba(255, 255, 255, 0.12);
        --bs-table-striped-bg: rgba(255, 255, 255, 0.03);
        color: rgba(240, 240, 240, 0.85);
        font-size: 0.9rem;
        vertical-align: middle;
    }

    .compare-table thead th {
        color: #0dfdb5;
        font-weight: 600;
        border-bottom-width: 1px;
        background: rgba(0, 0, 0, 0.2);
    }

    .compare-table th[scope="row"] {
        font-weight: 500;
        color: rgba(240, 240, 240, 0.9);
        width: 40%;
    }

    .compare-section-row th {
        background: rgba(0, 153, 120, 0.18);
        color: #f0f0f0;
        font-weight: 600;
        font-size: 0.85rem;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        padding-top: 0.75rem;
        padding-bottom: 0.75rem;
    }

    .compare-table td {
        color: rgba(240, 240, 240, 0.8);
    }

    .status-included {
        color: rgba(240, 240, 240, 0.9);
    }

    .status-not {
        color: rgba(255, 255, 255, 0.45);
    }

    /* Help Text */
    .help-text {
        color: rgba(255, 255, 255, 0.7);
        margin-top: 1rem;
    }

    /* Loading Spinner */
    .spinner-border-sm {
        width: 1rem;
        height: 1rem;
    }

    /* Custom Scrollbar */
    .SignupFormWrapper::-webkit-scrollbar {
        width: 6px;
    }

    .SignupFormWrapper::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.1);
        border-radius: 3px;
    }

    .SignupFormWrapper::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.3);
        border-radius: 3px;
    }

    .SignupFormWrapper::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 255, 255, 0.5);
    }

    /* Responsive */
    @media (max-width: 768px) {
        .SignupFormWrapper {
            padding: 30px 20px;
            margin: 10px;
        }

        .card-body {
            padding: 1.5rem;
        }

        .price-display {
            font-size: 1.5rem;
        }

        .compare-table {
            font-size: 0.8rem;
        }
    }
</style>
